/**
 * useActiveStorm — storm state resolver for both surfaces.
 *
 * NO `eta` FIELD BY DESIGN (2026-07-24 device walk). It used to re-export the
 * fixture's `etaDisplay` — a PRE-FORMATTED ENGLISH string ("Fri 24 Jul, 21:58")
 * — and five render sites consumed it: mobile home plus four web landing
 * components. Every one of them printed an English date inside an otherwise
 * translated sentence under es/ht, including the landing a cold reviewer sees
 * first. W2-m had fixed exactly this in `hurricane.tsx` and swept the FIXTURES
 * for baked dates, but the sweep followed the field's declaration, not its
 * propagation through this hook.
 *
 * Callers now receive only `etaIso` and MUST format it with their own locale tag
 * (`formatEtaLong(etaIso, tag)` for the long form, `formatStormEta` for short).
 * Removing the field rather than deprecating it is deliberate: it makes the
 * defect unrepresentable instead of merely discouraged.
 */

export type ActiveStormResult =
  | { active: true; name: string; etaIso: string }
  | { active: false };

export interface StormFixture {
  name: string;
  etaIso: string;
  etaDisplay: string;
}

/**
 * Resolves whether a storm is actively threatening, given current time and fixture data.
 *
 * @param now - Current wall-clock date (from `useNow()`)
 * @param storm - Storm fixture (from `getDemoStorm(now)`)
 * @param seasonActive - Whether hurricane season is active (from `useHurricaneSeason()`)
 */
export function useActiveStorm(now: Date, storm: StormFixture, seasonActive = true): ActiveStormResult {
  if (!seasonActive) {
    return { active: false };
  }

  const etaDate = new Date(storm.etaIso);
  const diffMs = etaDate.getTime() - now.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  if (diffDays < -2 || diffDays > 14) {
    return { active: false };
  }

  return {
    active: true,
    name: storm.name,
    etaIso: storm.etaIso,
  };
}
